import React from 'react';
import axios from 'axios';
import AnalysisProvenance from '../AnalysisProvenance';
import ConfidenceAudit from '../ConfidenceAudit';
import { API, BACKEND_URL, renderBoldText, getCompanyIcon, getMediaIcon } from '../../lib/helpers';
import { FaGlobe, FaUsers, FaBrain, FaShoppingCart, FaBuilding } from 'react-icons/fa';
import {
  TvIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { BrandLogo } from '../EnhancedLogoSystem';

/**
 * The full analysis results view — executive summary, TAM/SAM/SOM, segmentation,
 * competitive analysis, opportunities, threats, recommendations, data sources,
 * exports, and the history list.
 *
 * Extracted from App.js (Feb 2026, P2 monolith decomposition).
 * Style: legacy classes preserved 1:1 to avoid visual regression.
 * BCM design-system tokens will be applied in a follow-up pass.
 */
export default function AnalysisResults({
  analysis,
  history,
  formatCurrency,
  updateAnalysis,
  toggleSaveAnalysis,
  toggleVisibility,
  deleteAnalysis,
  rerunAnalysis,
  rerunFromHistory,
  loadAnalysis,
  loadHistory,
  setAnalysis,
  setCurrentStep,
  currentStep,
  exportPDF,
  exportPersonas,
  isProcessing,
  processingMessage,
}) {
  if (!analysis) return null;
  return (
        <React.Fragment>
          <div className="space-y-8">
            {/* Analysis Provenance (swarm trace) — always shown on results view */}
            <AnalysisProvenance swarmTrace={analysis.market_map?.swarm_trace} />
            {/* Confidence Audit — multi-pass synthesis claim audit */}
            <ConfidenceAudit confidenceSummary={analysis.market_map?.confidence_summary} />
            {/* Market Map Header */}
            {/* Market Map Header — BCM editorial treatment */}
            <div className="bcm-card !p-10 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-nordic" aria-hidden="true" />
              <div className="text-center mb-8 mt-2">
                <p className="bcm-label text-paradiso mb-3">BCM Market Intelligence</p>
                <h2 className="bcm-headline text-4xl sm:text-5xl mb-3">
                  {analysis.market_input.product_name}
                </h2>
                <p className="bcm-caption text-paradiso uppercase tracking-[0.18em] font-display font-semibold">
                  {analysis.market_input.geography} <span className="mx-2 text-silver">·</span> {analysis.market_input.industry}
                </p>

                {/* Save/Delete/Visibility Controls */}
                <div className="flex justify-center items-center gap-3 mt-6">
                  {/* Save Button */}
                  <button
                    onClick={() => {
                      const currentSaved = history.find(h => h.id === analysis.market_map.id)?.is_saved || false;
                      updateAnalysis(analysis.market_map.id, { is_saved: !currentSaved }).then(success => {
                        if (success) {
                          alert(currentSaved ? 'Analysis removed from saved' : 'Analysis saved!');
                          loadHistory();
                        }
                      });
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-bcm-md border font-display font-semibold text-sm transition-colors ${
                      history.find(h => h.id === analysis.market_map.id)?.is_saved
                        ? 'bg-sandy/15 border-sandy text-[#8a5a1e] hover:bg-sandy/25'
                        : 'bg-white border-silver text-paradiso hover:bg-silver/40'
                    }`}
                    data-testid="save-analysis-btn"
                  >
                    <svg className="w-5 h-5" fill={history.find(h => h.id === analysis.market_map.id)?.is_saved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                    </svg>
                    {history.find(h => h.id === analysis.market_map.id)?.is_saved ? 'Saved' : 'Save'}
                  </button>

                  {/* Visibility Toggle */}
                  <button
                    onClick={() => {
                      const currentVisibility = history.find(h => h.id === analysis.market_map.id)?.visibility || 'private';
                      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
                      updateAnalysis(analysis.market_map.id, { visibility: newVisibility }).then(success => {
                        if (success) {
                          alert(`Analysis is now ${newVisibility}`);
                          loadHistory();
                        }
                      });
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-bcm-md border font-display font-semibold text-sm transition-colors ${
                      history.find(h => h.id === analysis.market_map.id)?.visibility === 'public'
                        ? 'bg-billiard/15 border-billiard text-billiard hover:bg-billiard/25'
                        : 'bg-white border-silver text-paradiso hover:bg-silver/40'
                    }`}
                    data-testid="visibility-analysis-btn"
                  >
                    {history.find(h => h.id === analysis.market_map.id)?.visibility === 'public' ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Public
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Private
                      </>
                    )}
                  </button>
                  
                  {/* Delete Button */}
                  <button
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to delete this analysis for "${analysis.market_input.product_name}"? This cannot be undone.`)) {
                        axios.delete(`${API}/analysis/${analysis.market_map.id}`, { withCredentials: true })
                          .then(() => {
                            alert('Analysis deleted');
                            setAnalysis(null);
                            setCurrentStep(1);
                            loadHistory();
                          })
                          .catch(err => {
                            alert(err.response?.data?.detail || 'Error deleting analysis');
                          });
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-bcm-md border border-red-300 bg-white text-red-600 hover:bg-red-50 transition-colors font-display font-semibold text-sm"
                    data-testid="delete-analysis-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>

                  {/* Edit & Re-run Button */}
                  <button
                    onClick={rerunAnalysis}
                    className="flex items-center gap-2 px-4 py-2 rounded-bcm-md border border-bcm-orange bg-bcm-orange/10 text-bcm-orange hover:bg-bcm-orange hover:text-white transition-colors font-display font-semibold text-sm"
                    data-testid="rerun-analysis-btn"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Edit &amp; Re-run
                  </button>
                </div>

                {/* Headline KPIs — three pillars, BCM-styled */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-silver mt-10 rounded-bcm-lg overflow-hidden border border-silver">
                  <div className="bg-white px-6 py-8 text-center">
                    <p className="bcm-label text-paradiso mb-2">Total Market Size</p>
                    <p className="font-display font-black text-4xl tracking-[-0.02em] text-nordic">
                      {formatCurrency(analysis.market_map.total_market_size)}
                    </p>
                  </div>
                  <div className="bg-white px-6 py-8 text-center">
                    <p className="bcm-label text-paradiso mb-2">Annual Growth Rate</p>
                    <p className="font-display font-black text-4xl tracking-[-0.02em] text-billiard">
                      {(analysis.market_map.market_growth_rate * 100).toFixed(1)}%
                    </p>
                    <p className="bcm-caption mt-1">CAGR</p>
                  </div>
                  <div className="bg-white px-6 py-8 text-center">
                    <p className="bcm-label text-paradiso mb-2">Confidence</p>
                    <p className={`font-display font-black text-4xl tracking-[-0.02em] ${
                      analysis.market_map.confidence_level === 'high' ? 'text-billiard' :
                      analysis.market_map.confidence_level === 'medium' ? 'text-sandy' : 'text-paradiso'
                    }`}>
                      {analysis.market_map.confidence_level.charAt(0).toUpperCase() + analysis.market_map.confidence_level.slice(1)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Executive Summary — BCM editorial card */}
            <div className="bcm-card !p-10 relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-sandy" aria-hidden="true" />
              <header className="mb-10 mt-2 border-b border-silver pb-8">
                <p className="bcm-label text-paradiso mb-3">Strategic Memo</p>
                <h3 className="bcm-headline text-4xl sm:text-5xl mb-2">Executive Summary</h3>
                <p className="bcm-caption text-paradiso">Synthesised by BCM Agent Swarm · 17-agent multi-pass pipeline</p>
              </header>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                {/* Key Market Metrics */}
                <div className="border border-silver rounded-bcm-md p-6 bg-silver/20">
                  <p className="bcm-label text-paradiso mb-4">Market Opportunity</p>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between items-baseline">
                      <dt className="text-paradiso">Total market size</dt>
                      <dd className="font-display font-bold text-nordic">{formatCurrency(analysis.market_map.total_market_size)}</dd>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <dt className="text-paradiso">Growth rate</dt>
                      <dd className="font-display font-bold text-billiard">{(analysis.market_map.market_growth_rate * 100).toFixed(1)}% CAGR</dd>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <dt className="text-paradiso">Serviceable (SAM)</dt>
                      <dd className="font-display font-bold text-paradiso">{formatCurrency(analysis.market_map.total_market_size * 0.3)}</dd>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <dt className="text-paradiso">Target revenue (SOM)</dt>
                      <dd className="font-display font-bold text-bcm-orange">{formatCurrency(analysis.market_map.total_market_size * 0.03)}</dd>
                    </div>
                  </dl>
                </div>

                {/* Competitive Landscape */}
                <div className="border border-silver rounded-bcm-md p-6 bg-silver/20">
                  <p className="bcm-label text-paradiso mb-4">Competitive Dynamics</p>
                  <div className="space-y-3">
                    {analysis.market_map.competitors.slice(0, 4).map((competitor, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm font-display font-semibold text-black-stallion">{competitor.name}</span>
                        <span className="text-xs bcm-label bg-moonstone/15 text-paradiso px-2 py-1 rounded-bcm-sm">
                          {competitor.market_share ? `${(competitor.market_share * 100).toFixed(0)}%` : 'n/a'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-silver">
                    <p className="bcm-caption">
                      {analysis.market_map.competitors.length} key players · room for differentiation
                    </p>
                  </div>
                </div>

                {/* Strategic Priorities */}
                <div className="border border-silver rounded-bcm-md p-6 bg-silver/20">
                  <p className="bcm-label text-paradiso mb-4">Key Drivers &amp; Trends</p>
                  <div className="space-y-3">
                    {analysis.market_map.key_drivers.slice(0, 4).map((driver, index) => (
                      <div key={index} className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-billiard rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-sm text-black-stallion leading-relaxed">{driver}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-silver">
                    <p className="text-xs text-gray-600">
                      Primary growth driven by {analysis.market_input.demand_driver}
                    </p>
                  </div>
                </div>
              </div>

              {/* Detailed Executive Summary Text — editorial prose */}
              {analysis.market_map && analysis.market_map.executive_summary ? (
                <>
                <article className="max-w-3xl mx-auto bcm-body text-[0.95rem]">
                  {analysis.market_map.executive_summary.split('\n\n').map((section, sectionIndex) => (
                    <div key={sectionIndex} className="mb-8">
                      {section.split('\n').map((line, lineIndex) => {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) return null;

                        // Header (wrapped in ** **)
                        if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                          const headerText = trimmedLine.replace(/\*\*/g, '');
                          return (
                            <h4
                              key={lineIndex}
                              className="font-display font-bold text-lg text-nordic mt-10 mb-4 pb-2 border-b border-silver"
                            >
                              {headerText}
                            </h4>
                          );
                        }

                        // Bullet line
                        if (trimmedLine.match(/^[-•*]\s+/) || trimmedLine.match(/^\d+\.\s+/)) {
                          return (
                            <div key={lineIndex} className="flex items-start mb-3 ml-2">
                              <span className="w-1.5 h-1.5 bg-bcm-orange rounded-full mt-2.5 mr-4 flex-shrink-0" />
                              <p className="flex-1">
                                {renderBoldText(trimmedLine.replace(/^[-•*]\s+/, '').replace(/^\d+\.\s+/, ''))}
                              </p>
                            </div>
                          );
                        }

                        // Regular paragraph
                        return (
                          <p key={lineIndex} className="mb-4">
                            {renderBoldText(trimmedLine)}
                          </p>
                        );
                      })}
                    </div>
                  ))}
                </article>

                  {/* Analysis Metadata */}
                  <div className="mt-10 pt-6 border-t border-silver flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 bcm-caption">
                      <span>Analysis date: <span className="font-display font-semibold text-black-stallion">{new Date(analysis.market_map.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span></span>
                      <span className={`bcm-label px-2 py-1 rounded-bcm-sm ${
                        analysis.market_map.confidence_level === 'high' ? 'bg-billiard/15 text-billiard' :
                        analysis.market_map.confidence_level === 'medium' ? 'bg-sandy/20 text-[#8a5a1e]' : 'bg-paradiso/10 text-paradiso'
                      }`}>
                        {analysis.market_map.confidence_level} confidence
                      </span>
                    </div>
                  </div>
                  
                  {/* Footnote Sources */}
                  <div className="mt-6 pt-4 border-t border-gray-100 bg-gray-50 -mx-8 px-8 py-4">
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Sources: </span>
                      {analysis.market_map.data_sources.slice(0, 4).map((source, index) => {
                        const sourceName = typeof source === 'string' ? source : source.name || source;
                        const sourceUrl = typeof source === 'object' && source.url ? source.url : null;
                        
                        return (
                          <span key={index}>
                            {index > 0 && ', '}
                            {sourceUrl ? (
                              <a 
                                href={sourceUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 underline"
                              >
                                {sourceName}
                              </a>
                            ) : (
                              <span>{sourceName}</span>
                            )}
                          </span>
                        );
                      })}
                      {analysis.market_map.data_sources.length > 4 && (
                        <span className="italic"> and {analysis.market_map.data_sources.length - 4} other sources</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-paradiso italic text-center py-8 bcm-body">
                  Executive summary will be generated with the market analysis
                </div>
              )}
            </div>

            {/* TAM → SAM → SOM Visual Chart - Moved after Executive Summary */}
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Market Size Analysis</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left side - Detailed Analytical Descriptions */}
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-4 h-4 bg-blue-900 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-bold text-blue-900 mb-2">TAM - {formatCurrency(analysis.market_map.total_market_size)}</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Total Addressable Market:</strong> Represents 100% of global revenue opportunity for {analysis.market_input.product_name}{' '}
                        across all customer segments in {analysis.market_input.geography}. Based on{' '}
                        {analysis.market_map.data_sources.slice(0, 3).map((source, idx) => {
                          const sourceMapping = {
                            "Gartner Market Research": "https://www.gartner.com/en/research",
                            "McKinsey Industry Reports": "https://www.mckinsey.com/industries",
                            "IBISWorld Market Analysis": "https://www.ibisworld.com",
                            "Forrester Research": "https://www.forrester.com/research",
                            "PwC Industry Insights": "https://www.pwc.com/us/en/industries.html",
                            "Statista": "https://www.statista.com",
                            "CB Insights": "https://www.cbinsights.com"
                          };
                          
                          const sourceName = typeof source === 'string' ? source : source.name;
                          const sourceUrl = typeof source === 'object' && source.url ? source.url : sourceMapping[sourceName];
                          
                          return (
                            <span key={idx}>
                              {idx > 0 && (idx === analysis.market_map.data_sources.slice(0, 3).length - 1 ? ' and ' : ', ')}
                              {sourceUrl ? (
                                <a 
                                  href={sourceUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline"
                                  title={`Visit ${sourceName}`}
                                >
                                  {sourceName}
                                </a>
                              ) : (
                                <span>{sourceName}</span>
                              )}
                              <sup className="text-blue-600">[{idx + 1}]</sup>
                            </span>
                          );
                        })}
                        {' '}data, growing at {(analysis.market_map.market_growth_rate * 100).toFixed(1)}% CAGR driven by {analysis.market_map.key_drivers.slice(0,2).join(' and ')}.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-4 h-4 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-bold text-blue-600 mb-2">SAM - {formatCurrency(analysis.market_map.total_market_size * 0.3)}</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Serviceable Addressable Market:</strong> 30% of TAM ({formatCurrency(analysis.market_map.total_market_size * 0.3)}){' '}
                        representing segments we can realistically serve with our {analysis.market_input.transaction_type} business model.{' '}
                        Focused on {analysis.market_input.target_user} who prioritize {analysis.market_input.key_metrics.split(',')[0]}{' '}
                        and are motivated by {analysis.market_input.demand_driver}.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-4 h-4 bg-blue-300 rounded-full mt-2 flex-shrink-0"></div>
                    <div>
                      <h4 className="text-lg font-bold text-blue-300 mb-2">SOM - {formatCurrency(analysis.market_map.total_market_size * 0.03)}</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        <strong>Serviceable Obtainable Market:</strong> 10% of SAM ({formatCurrency(analysis.market_map.total_market_size * 0.03)}){' '}
                        based on realistic 3-5 year market penetration assuming competitive response from {analysis.market_map.competitors.slice(0,2).map(c => c.name).join(' and ')}.{' '}
                        Achievable through {analysis.market_map.strategic_recommendations.slice(0,1)[0]?.toLowerCase() || 'focused market strategy'}.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right side - Concentric Circles Visualization with Better Labels and Arrows */}
                <div className="flex justify-center relative">
                  <div className="relative w-80 h-80">
                    {/* TAM - Outer Circle */}
                    <div className="absolute inset-0 w-80 h-80 bg-blue-900 rounded-full flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-xl font-bold">{formatCurrency(analysis.market_map.total_market_size)}</div>
                        <div className="text-sm font-medium">TAM</div>
                      </div>
                    </div>
                      
                    {/* SAM - Middle Circle - Properly centered */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-lg font-bold">{formatCurrency(analysis.market_map.total_market_size * 0.3)}</div>
                        <div className="text-sm font-medium">SAM</div>
                      </div>
                    </div>
                        
                    {/* SOM - Inner Circle - Properly centered */}
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-blue-300 rounded-full flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-base font-bold">{formatCurrency(analysis.market_map.total_market_size * 0.03)}</div>
                        <div className="text-xs font-medium">SOM</div>
                      </div>
                    </div>
                    
                    {/* Simple labeling: TAM top, SAM right, SOM in center */}
                    
                    {/* TAM - Above pointing to outermost circle */}
                    <div className="absolute -top-12 left-1/2 transform -translate-x-1/2">
                      <div className="flex flex-col items-center">
                        <div className="text-sm font-semibold text-gray-700 mb-2">TAM</div>
                        <div className="w-0 h-0 border-l-3 border-r-3 border-t-4 border-transparent border-t-gray-500"></div>
                      </div>
                    </div>
                    
                    {/* SAM - Right side pointing to middle circle */}
                    <div className="absolute top-1/2 -right-12 transform -translate-y-1/2">
                      <div className="flex items-center">
                        <div className="text-sm font-semibold text-gray-700 mr-2">SAM</div>
                        <div className="w-0 h-0 border-t-3 border-b-3 border-l-4 border-transparent border-l-gray-500"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Detailed Market Analysis Summary */}
              <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Market Sizing Methodology & Assumptions</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-900 mb-2">{formatCurrency(analysis.market_map.total_market_size)}</div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Total Addressable Market</div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      100% market opportunity based on {analysis.market_map.methodology}. 
                      Growth rate: {(analysis.market_map.market_growth_rate * 100).toFixed(1)}% CAGR.
                      Confidence: {analysis.market_map.confidence_level}.
                    </div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">{formatCurrency(analysis.market_map.total_market_size * 0.3)}</div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Serviceable Addressable Market</div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      30% of TAM filtering for {analysis.market_input.target_user} segments compatible with 
                      {analysis.market_input.transaction_type} model and {analysis.market_input.key_metrics.split(',')[0]} requirements.
                    </div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-300 mb-2">{formatCurrency(analysis.market_map.total_market_size * 0.03)}</div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Serviceable Obtainable Market</div>
                    <div className="text-xs text-gray-600 leading-relaxed">
                      10% of SAM assuming competitive market with established players 
                      ({analysis.market_map.competitors.slice(0,2).map(c => c.name).join(', ')}) and 3-5 year penetration timeline.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Market Map */}
            {analysis.visual_map && (
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{analysis.visual_map.title}</h3>
                
                {/* Market Segmentation Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Geographic Segmentation */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-3">
                        <FaGlobe className="text-2xl text-blue-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Geographic Segmentation</h4>
                      <div className="w-16 h-1 bg-blue-500 mx-auto mt-2"></div>
                      <p className="text-xs text-gray-600 mt-2">Country, City, Density, Language, Climate, Area, Population</p>
                    </div>
                    
                    <div className="space-y-4">
                      {analysis.visual_map.geographic_segments && analysis.visual_map.geographic_segments.map((segment, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center mb-2">
                            <span className="text-xl mr-3">{segment.icon}</span>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{segment.name}</div>
                              <div className="text-sm text-gray-600">{segment.description}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                            <div className="flex justify-between">
                              <span className="font-medium">Market Size:</span>
                              <span className="text-blue-600 font-semibold">{formatCurrency(segment.size)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Growth:</span>
                              <span className="text-green-600 font-semibold">{(segment.growth * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          {segment.key_players && segment.key_players.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">Key Players:</div>
                              <div className="flex flex-wrap gap-1">
                                {segment.key_players.slice(0, 3).map((player, i) => (
                                  <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                    <span className="mr-1">{getCompanyIcon(player)}</span>
                                    {player}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Demographic Segmentation */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-3">
                        <FaUsers className="text-2xl text-orange-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Demographic Segmentation</h4>
                      <div className="w-16 h-1 bg-orange-500 mx-auto mt-2"></div>
                      <p className="text-xs text-gray-600 mt-2">Age, Gender, Income, Education, Social Status, Family, Life Stage, Occupation</p>
                    </div>
                    
                    <div className="space-y-4">
                      {analysis.visual_map.demographic_segments && analysis.visual_map.demographic_segments.map((segment, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center mb-2">
                            <span className="text-xl mr-3">{segment.icon}</span>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{segment.name}</div>
                              <div className="text-sm text-gray-600">{segment.description}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                            <div className="flex justify-between">
                              <span className="font-medium">Market Size:</span>
                              <span className="text-orange-600 font-semibold">{formatCurrency(segment.size)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Growth:</span>
                              <span className="text-green-600 font-semibold">{(segment.growth * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          {segment.key_players && segment.key_players.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">Key Players:</div>
                              <div className="flex flex-wrap gap-1">
                                {segment.key_players.slice(0, 3).map((player, i) => (
                                  <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                    <span className="mr-1">{getCompanyIcon(player)}</span>
                                    {player}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Psychographic Segmentation */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 mb-3">
                        <FaBrain className="text-2xl text-yellow-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Psychographic Segmentation</h4>
                      <div className="w-16 h-1 bg-yellow-500 mx-auto mt-2"></div>
                      <p className="text-xs text-gray-600 mt-2">Lifestyle, AIO (Activity/Interest/Opinion), Concerns, Personality, Values, Attitudes</p>
                    </div>
                    
                    <div className="space-y-4">
                      {analysis.visual_map.psychographic_segments && analysis.visual_map.psychographic_segments.map((segment, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center mb-2">
                            <span className="text-xl mr-3">{segment.icon}</span>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{segment.name}</div>
                              <div className="text-sm text-gray-600">{segment.description}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                            <div className="flex justify-between">
                              <span className="font-medium">Market Size:</span>
                              <span className="text-yellow-600 font-semibold">{formatCurrency(segment.size)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Growth:</span>
                              <span className="text-green-600 font-semibold">{(segment.growth * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          {segment.key_players && segment.key_players.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">Key Players:</div>
                              <div className="flex flex-wrap gap-1">
                                {segment.key_players.slice(0, 3).map((player, i) => (
                                  <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                    <span className="mr-1">{getCompanyIcon(player)}</span>
                                    {player}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Behavioral Segmentation */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-purple-100 mb-3">
                        <FaShoppingCart className="text-2xl text-purple-600" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">Behavioral Segmentation</h4>
                      <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
                      <p className="text-xs text-gray-600 mt-2">Purchase, Usage, Intent, Occasion, Buyer Stage, Life Cycle Stage, Engagement</p>
                    </div>
                    
                    <div className="space-y-4">
                      {analysis.visual_map.behavioral_segments && analysis.visual_map.behavioral_segments.map((segment, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center mb-2">
                            <span className="text-xl mr-3">{segment.icon}</span>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{segment.name}</div>
                              <div className="text-sm text-gray-600">{segment.description}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                            <div className="flex justify-between">
                              <span className="font-medium">Market Size:</span>
                              <span className="text-purple-600 font-semibold">{formatCurrency(segment.size)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium">Growth:</span>
                              <span className="text-green-600 font-semibold">{(segment.growth * 100).toFixed(1)}%</span>
                            </div>
                          </div>
                          {segment.key_players && segment.key_players.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-medium text-gray-600 mb-1">Key Players:</div>
                              <div className="flex flex-wrap gap-1">
                                {segment.key_players.slice(0, 3).map((player, i) => (
                                  <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                    <span className="mr-1">{getCompanyIcon(player)}</span>
                                    {player}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Firmographic Segmentation - B2B Only */}
                {analysis.market_map.segmentation_by_firmographics && analysis.market_map.segmentation_by_firmographics.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">B2B Firmographic Segmentation</h4>
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-teal-100 mb-3">
                          <FaBuilding className="text-2xl text-teal-600" />
                        </div>
                        <h4 className="text-lg font-bold text-gray-900">Firmographic Segmentation</h4>
                        <div className="w-16 h-1 bg-teal-500 mx-auto mt-2"></div>
                        <p className="text-xs text-gray-600 mt-2">Industry, Company Size, Geographic Location, Job Titles/Roles, Company Revenue</p>
                      </div>
                      
                      <div className="space-y-4">
                        {analysis.market_map.segmentation_by_firmographics.map((segment, index) => (
                          <div key={index} className="bg-white p-4 rounded-lg border">
                            <div className="flex items-center mb-2">
                              <FaBuilding className="text-xl mr-3 text-teal-600" />
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">{segment.name}</div>
                                <div className="text-sm text-gray-600">{segment.description}</div>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                              <div className="flex justify-between">
                                <span className="font-medium">Market Size:</span>
                                <span className="text-teal-600 font-semibold">{formatCurrency(segment.size_estimate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="font-medium">Growth:</span>
                                <span className="text-green-600 font-semibold">{(segment.growth_rate * 100).toFixed(1)}%</span>
                              </div>
                            </div>
                            {segment.key_players && segment.key_players.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs font-medium text-gray-600 mb-1">Key Players:</div>
                                <div className="flex flex-wrap gap-1">
                                  {segment.key_players.slice(0, 3).map((player, i) => (
                                    <span key={i} className="inline-flex items-center bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                      <span className="mr-1">{getCompanyIcon(player)}</span>
                                      {player}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

 

            {/* Competitive Benchmarking */}
            {analysis.market_map.competitors && analysis.market_map.competitors.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Competitive Benchmarking</h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-4 px-3 font-bold text-gray-900 bg-gray-50">Brand</th>
                        <th className="text-left py-4 px-3 font-bold text-gray-900 bg-gray-50">Market Share</th>
                        <th className="text-left py-4 px-3 font-bold text-gray-900 bg-gray-50">Core Strengths</th>
                        <th className="text-left py-4 px-3 font-bold text-gray-900 bg-gray-50">Weaknesses</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.market_map.competitors.map((competitor, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-3">
                            <div className="flex items-center">
                              <div className="w-8 h-8 mr-3 flex items-center justify-center bg-gray-50 border border-gray-200 rounded">
                                <BrandLogo companyName={competitor.name} size={24} />
                              </div>
                              <div>
                                <div className="font-bold text-gray-900">{competitor.name}</div>
                                {competitor.website_url && (
                                  <a 
                                    href={competitor.website_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    {competitor.website_url.replace('https://', '').replace('www.', '')}
                                  </a>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-3">
                            {competitor.market_share ? (
                              <span className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                {(competitor.market_share * 100).toFixed(0)}%
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">N/A</span>
                            )}
                          </td>
                          <td className="py-4 px-3">
                            <div className="flex flex-wrap gap-1">
                              {competitor.strengths.map((strength, i) => (
                                <span key={i} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                  {strength}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="py-4 px-3">
                            <div className="flex flex-wrap gap-1">
                              {competitor.weaknesses.map((weakness, i) => (
                                <span key={i} className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                                  {weakness}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Competitive Digital Marketing Assessment */}
            {analysis.market_map.competitive_digital_assessment && Object.keys(analysis.market_map.competitive_digital_assessment).length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <FaGlobe className="mr-3 text-blue-600" />
                  Competitive Digital Marketing Assessment
                </h3>
                
                {/* Qualitative Assessment Disclaimer */}
                <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-1">Qualitative Competitive Intelligence</p>
                      <p>
                        This assessment provides qualitative observations about competitors' digital marketing strategies based on publicly observable presence. 
                        Specific metrics (ad spend, follower counts, traffic numbers) are not included as they require real-time API access. 
                        For precise competitive metrics, consider integrating live data sources or conducting primary research.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Object.entries(analysis.market_map.competitive_digital_assessment).map(([companyName, digitalData], index) => (
                    <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                      {/* Company Header */}
                      <div className="flex items-center mb-4 pb-3 border-b border-blue-200">
                        <div className="w-10 h-10 mr-3 flex items-center justify-center bg-white border border-blue-300 rounded-lg shadow-sm">
                          <BrandLogo companyName={companyName} size={32} />
                        </div>
                        <h4 className="font-bold text-gray-900 text-lg">{companyName}</h4>
                      </div>
                      
                      {/* Digital Strategy Overview */}
                      <div className="space-y-4">
                        {/* Digital Marketing Strategy */}
                        {digitalData.digital_marketing_strategy && (
                          <div className="bg-white p-3 rounded border border-blue-100">
                            <div className="text-xs font-medium text-blue-700 mb-1 flex items-center">
                              <FaBrain className="mr-1" />
                              STRATEGY OVERVIEW
                            </div>
                            <div className="text-xs text-gray-700">{digitalData.digital_marketing_strategy}</div>
                          </div>
                        )}
                        
                        {/* Primary Channels */}
                        {digitalData.primary_channels && digitalData.primary_channels.length > 0 && (
                          <div className="bg-white p-3 rounded border border-blue-100">
                            <div className="text-xs font-medium text-blue-700 mb-2 flex items-center">
                              <TvIcon className="w-4 h-4 mr-1" />
                              PRIMARY CHANNELS
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {digitalData.primary_channels.slice(0, 4).map((channel, i) => (
                                <div key={i} className="flex items-center bg-blue-50 text-blue-800 px-2 py-1 rounded text-xs">
                                  <span className="mr-1">{getMediaIcon(channel)}</span>
                                  {channel}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Social Media Presence */}
                        {digitalData.social_media_presence && (
                          <div className="bg-white p-3 rounded border border-blue-100">
                            <div className="text-xs font-medium text-blue-700 mb-1 flex items-center">
                              <FaBuilding className="mr-1" />
                              SOCIAL MEDIA
                            </div>
                            <div className="text-xs text-gray-700">{digitalData.social_media_presence}</div>
                          </div>
                        )}
                        
                        {/* SEO Positioning */}
                        {digitalData.seo_positioning && (
                          <div className="bg-white p-3 rounded border border-blue-100">
                            <div className="text-xs font-medium text-blue-700 mb-1 flex items-center">
                              <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                              SEO STRENGTH
                            </div>
                            <div className="text-xs text-gray-700">{digitalData.seo_positioning}</div>
                          </div>
                        )}
                        
                        {/* Marketing Strengths & Gaps */}
                        <div className="grid grid-cols-2 gap-3">
                          {digitalData.marketing_strengths && digitalData.marketing_strengths.length > 0 && (
                            <div className="bg-green-50 p-3 rounded border border-green-200">
                              <div className="text-xs font-medium text-green-700 mb-2 flex items-center">
                                <CheckCircleIcon className="w-4 h-4 mr-1" />
                                STRENGTHS
                              </div>
                              <div className="space-y-1">
                                {digitalData.marketing_strengths.slice(0, 2).map((strength, i) => (
                                  <div key={i} className="text-xs text-green-800">{strength}</div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {digitalData.marketing_gaps && digitalData.marketing_gaps.length > 0 && (
                            <div className="bg-red-50 p-3 rounded border border-red-200">
                              <div className="text-xs font-medium text-red-700 mb-2 flex items-center">
                                <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                                GAPS
                              </div>
                              <div className="space-y-1">
                                {digitalData.marketing_gaps.slice(0, 2).map((gap, i) => (
                                  <div key={i} className="text-xs text-red-800">{gap}</div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center mb-2">
                    <FaBrain className="text-purple-600 mr-2" />
                    <span className="font-medium text-purple-900">Digital Marketing Intelligence</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    This competitive assessment analyzes digital marketing strategies, advertising spend estimates, channel preferences, and marketing execution strengths/gaps to identify opportunities for differentiation and growth.
                  </p>
                </div>
              </div>
            )}

 

            {/* Strategic Analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Opportunities */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <FaUsers className="mr-2 text-orange-600" />
                  Market Opportunities
                </h3>
                <div className="space-y-4">
                  {analysis.market_map.opportunities.map((opportunity, index) => {
                    const parts = opportunity.split('**').filter(Boolean);
                    const title = parts.length > 1 ? parts[0].replace(/^:?\s*/, '').replace(/:?\s*$/, '') : null;
                    const body = parts.length > 1 ? parts.slice(1).join('').replace(/^:?\s*/, '') : opportunity;
                    return (
                      <div key={index} className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500" data-testid={`opportunity-${index}`}>
                        {title && <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>}
                        <p className="text-gray-700 text-sm leading-relaxed">{body}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Threats */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <FaShoppingCart className="mr-2 text-red-600" />
                  Market Threats
                </h3>
                <div className="space-y-4">
                  {analysis.market_map.threats.map((threat, index) => {
                    const parts = threat.split('**').filter(Boolean);
                    const title = parts.length > 1 ? parts[0].replace(/^:?\s*/, '').replace(/:?\s*$/, '') : null;
                    const body = parts.length > 1 ? parts.slice(1).join('').replace(/^:?\s*/, '') : threat;
                    return (
                      <div key={index} className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500" data-testid={`threat-${index}`}>
                        {title && <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>}
                        <p className="text-gray-700 text-sm leading-relaxed">{body}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <FaBuilding className="mr-2 text-green-600" />
                Strategic Recommendations
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {analysis.market_map.strategic_recommendations.map((recommendation, index) => {
                  const parts = recommendation.split('**').filter(Boolean);
                  const title = parts.length > 1 ? parts[0].replace(/^:?\s*/, '').replace(/:?\s*$/, '') : null;
                  const body = parts.length > 1 ? parts.slice(1).join('').replace(/^:?\s*/, '') : recommendation;
                  return (
                    <div key={index} className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500" data-testid={`recommendation-${index}`}>
                      <div className="flex items-center mb-2">
                        <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">{index + 1}</span>
                        {title && <h4 className="font-semibold text-gray-900">{title}</h4>}
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">{body}</p>
                    </div>
                  );
                })}
              </div>
            </div>


          </div>

        {/* Data Sources & Methodology - Moved to Bottom */}
        {analysis && (
          <div className="bg-white rounded-lg shadow-md p-6 mt-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <FaBrain className="mr-2 text-blue-600" />
              Resources & Footnotes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <FaBrain className="mr-2 text-blue-600" />
                  Data Sources & Citations
                </h4>
                <div className="space-y-4">
                  {analysis.market_map.data_sources.map((source, index) => {
                    // Map source names to URLs for backward compatibility
                    const sourceMapping = {
                      "Gartner Market Research": "https://www.gartner.com/en/research",
                      "McKinsey Industry Reports": "https://www.mckinsey.com/industries",
                      "IBISWorld Market Analysis": "https://www.ibisworld.com",
                      "Forrester Research": "https://www.forrester.com/research",
                      "PwC Industry Insights": "https://www.pwc.com/us/en/industries.html",
                      "Statista": "https://www.statista.com",
                      "CB Insights": "https://www.cbinsights.com",
                      "Crunchbase": "https://www.crunchbase.com"
                    };
                    
                    const sourceName = typeof source === 'string' ? source : source.name;
                    const sourceUrl = typeof source === 'object' && source.url ? source.url : sourceMapping[sourceName];
                    const citation = typeof source === 'object' ? source.citation : null;
                    const relevance = typeof source === 'object' ? source.relevance : null;
                    const reportType = typeof source === 'object' ? source.report_type : null;
                    const access = typeof source === 'object' ? source.access : null;
                    
                    return (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start">
                          <span className="mr-3 text-sm font-bold text-blue-600 flex-shrink-0">[{index + 1}]</span>
                          <div className="flex-1">
                            <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
                              <div className="flex-1 min-w-0">
                                {sourceUrl ? (
                                  <a 
                                    href={sourceUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-semibold underline hover:no-underline transition-colors inline-flex items-center"
                                    title={`Visit ${sourceName}`}
                                  >
                                    {sourceName}
                                    <svg className="w-4 h-4 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                ) : (
                                  <span className="font-semibold text-gray-900">{sourceName}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {reportType && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                                    {reportType}
                                  </span>
                                )}
                                {access && (
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                                    access.includes('Free') ? 'bg-green-100 text-green-700' : 
                                    access === 'Not Applicable' ? 'bg-gray-100 text-gray-700' :
                                    'bg-orange-100 text-orange-700'
                                  }`}>
                                    {access}
                                  </span>
                                )}
                              </div>
                            </div>
                            {citation && (
                              <p className="text-sm text-gray-700 mb-2 italic">
                                <span className="font-medium text-gray-600">Citation:</span> {citation}
                              </p>
                            )}
                            {relevance && (
                              <p className="text-xs text-gray-600">
                                <span className="font-medium text-gray-700">Usage:</span> {relevance}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Data Transparency Notice */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-xs text-blue-900">
                      <p className="font-semibold mb-1">AI-Powered Market Intelligence</p>
                      <p>
                        This analysis is generated by the BCM Agent Swarm (GPT-4o with Claude Sonnet 4 fallback, specialized sub-agents for sizing, competitive intelligence, Resonate rAI segmentation, opportunities, threats, and strategy) using industry knowledge and market research methodologies. 
                        While sources reference credible industry reports and data, this should complement—not replace—primary research 
                        and due diligence for critical business decisions. Source URLs are constructed based on typical report structures 
                        for the referenced publishers.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">🔬 Methodology</h4>
                <p className="text-sm text-gray-600">{analysis.market_map.methodology}</p>
                <div className="mt-4">
                  <span className="text-xs font-medium text-gray-500">Analysis Date: </span>
                  <span className="text-xs text-gray-600">
                    {new Date(analysis.market_map.timestamp).toLocaleDateString()}
                  </span>
                </div>
                <div className="mt-2">
                  <span className="text-xs font-medium text-gray-500">Confidence Level: </span>
                  <span className="text-xs text-gray-600 capitalize">
                    {analysis.market_map.confidence_level}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1. Resonate rAI Integration - Moved to Bottom */}
        {analysis && analysis.market_map && (
          <div className="mt-8 bg-white rounded-lg shadow-md p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Resonate rAI Integration</h3>
              <button
                onClick={() => exportPersonas(analysis.market_map.id)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {processingMessage}
                  </span>
                ) : (
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export for Resonate rAI
                  </span>
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Description */}
              <div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-lg border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Audience Intelligence
                  </h4>
                  <p className="text-purple-700 text-sm leading-relaxed mb-4">
                    Transform market segmentation into actionable audience insights using Resonate's AI-powered consumer intelligence platform.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-700">Demographics & psychographic mapping</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-700">Media consumption & channel preferences</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-700">Behavioral patterns & motivations</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-gray-700">Resonate Elements taxonomy integration</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right: Integration Details */}
              <div>
                <h4 className="font-bold text-gray-900">Ready for Resonate rAI Platform</h4>
                <p className="text-gray-600 text-sm mb-4">
                Export generates JSON data with base-level demographics, geographics, and media usage mapped directly to Resonate Elements taxonomy. 
                Each segment includes specific taxonomy paths for easy entry into the Resonate rAI platform to recreate audiences.
                </p>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Export Format:</span> Structured JSON with taxonomy paths
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Integration:</span> Direct Resonate Elements mapping
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Segments:</span> Demographics • Psychographics • Behavioral
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Confidence:</span> High/Medium/Low mapping accuracy
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Export Panel — BCM dark surface, monochrome treatment */}
        {analysis && analysis.market_map && (
          <div className="mt-8 bcm-card-dark !p-8">
            <div className="mb-6">
              <p className="bcm-label text-moonstone mb-2">Deliverables</p>
              <h3 className="font-display font-bold text-2xl text-white mb-1">Download the market analysis</h3>
              <p className="text-sm text-silver">Three formats — strategy memo, client-ready slides, and the underlying data.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href={`${BACKEND_URL}/api/export-pdf/${analysis.market_map.id}`}
                download={`BCM-Market-Report-${analysis.market_input.product_name.replace(/\s+/g, '-')}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-pdf-btn"
                className="group flex flex-col items-start gap-3 px-6 py-5 rounded-bcm-md bg-white/5 hover:bg-bcm-orange transition-colors border border-white/10 hover:border-bcm-orange"
              >
                <span className="bcm-label text-moonstone group-hover:text-white">PDF</span>
                <span className="font-display font-bold text-lg text-white">Strategy memo</span>
                <span className="text-xs text-silver group-hover:text-white/90">Client-ready report</span>
              </a>
              <a
                href={`${BACKEND_URL}/api/export-ppt/${analysis.market_map.id}`}
                download={`BCM-Market-Analysis-${analysis.market_input.product_name.replace(/\s+/g, '-')}.pptx`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-powerpoint-btn"
                className="group flex flex-col items-start gap-3 px-6 py-5 rounded-bcm-md bg-white/5 hover:bg-bcm-orange transition-colors border border-white/10 hover:border-bcm-orange"
              >
                <span className="bcm-label text-moonstone group-hover:text-white">PowerPoint</span>
                <span className="font-display font-bold text-lg text-white">Presentation deck</span>
                <span className="text-xs text-silver group-hover:text-white/90">BCM-branded slides</span>
              </a>
              <a
                href={`${BACKEND_URL}/api/export-market-map/${analysis.market_map.id}`}
                download={`${analysis.market_input.product_name}_market_analysis.xlsx`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-excel-btn"
                className="group flex flex-col items-start gap-3 px-6 py-5 rounded-bcm-md bg-white/5 hover:bg-bcm-orange transition-colors border border-white/10 hover:border-bcm-orange"
              >
                <span className="bcm-label text-moonstone group-hover:text-white">Excel</span>
                <span className="font-display font-bold text-lg text-white">Source data</span>
                <span className="text-xs text-silver group-hover:text-white/90">Detailed worksheets</span>
              </a>
            </div>
            <p className="bcm-caption text-moonstone mt-5">
              All exports run server-side. If a download doesn&apos;t start in a few seconds, right-click the link and choose &quot;Save link as&quot;.
            </p>
          </div>
        )}
        </React.Fragment>
  );
}
